import { Link, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  allauthErrorMessage,
  useResetPassword,
  useResetPasswordKeyCheck,
} from "@/lib/auth";
import { safeReturnPath } from "@/lib/return-path";

const schema = z
  .object({
    password: z.string().min(8, "Use at least 8 characters"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "Passwords do not match",
  });

type FormValues = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const { key = "" } = useParams();
  const [searchParams] = useSearchParams();
  const returnPath = safeReturnPath(searchParams.get("next"));
  const signInHref = returnPath
    ? `/?${new URLSearchParams({ next: returnPath }).toString()}`
    : "/";
  const keyCheck = useResetPasswordKeyCheck(key);
  const reset = useResetPassword();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  const onSubmit = form.handleSubmit(async ({ password }) => {
    try {
      await reset.mutateAsync({ key, password });
    } catch (err) {
      form.setError("password", {
        message: allauthErrorMessage(err, "Could not reset your password"),
      });
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Choose a new password</CardTitle>
          <CardDescription>Enter and confirm your new password below.</CardDescription>
        </CardHeader>
        <CardContent>
          {keyCheck.isPending ? (
            <p className="text-sm text-muted-foreground">Checking your reset link…</p>
          ) : keyCheck.isError ? (
            <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              This reset link is invalid or has expired.{" "}
              <Link to="/auth/forgot-password" className="font-medium underline">
                Request a new one
              </Link>
              .
            </p>
          ) : reset.isSuccess ? (
            <p className="rounded-md bg-success/10 p-3 text-sm text-success">
              Your password has been updated.{" "}
              <Link to={signInHref} className="font-medium underline">
                Sign in
              </Link>{" "}
              with your new password.
            </p>
          ) : (
            <Form {...form}>
              <form onSubmit={onSubmit} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm new password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={reset.isPending}>
                  {reset.isPending ? "Updating…" : "Update password"}
                </Button>
              </form>
            </Form>
          )}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            <Link to={signInHref} className="hover:underline">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
